'use client';

import React, { useEffect, useState } from 'react';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocalization } from '@/ui/providers/localizationProvider';
import i18nCountries from 'i18n-iso-countries';
import axios from 'axios';
import { apiPaths } from '@/constants/apiPaths';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useCaptchaHook } from '@aacn.eu/use-friendly-captcha';
import {
  CaptchaValidationErrorCodes,
  FRIENDLY_CAPTCHA_ENDPOINT,
} from '@/integrations/captcha/constants';
import { DataLayerHelper } from '@/utils/data-layer-helper';
import { buildTrackingId } from '@/utils/tracking-helper';
import GreenCheckmark from '../../../../../public/images/checkmark-green.svg';
import { SelectInput } from '../../shared/inputs/Dropdown/SelectInput';
import TextInput from '../../shared/inputs/TextInput';
import Button from '../../shared/Button';
import PrivacyPolicyCheckBox from '../ClubRegistration/components/PrivacyPolicyCheckBox';
import { B2BFormData } from './types/B2BFormData';
import OptimizedImage from '../../shared/OptimizedImage';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import { B2BContactFormTile } from './types/B2BContactFormTile';
import Container from '../../shared/Container';

export default function B2BContactForm({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: B2BContactFormTile;
  trackingPrefix?: string;
}>) {
  const t = useTranslations('B2BContactForm');
  const captchaMessages = useTranslations('captchaMessages');
  const emailTo = tile.email.text;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [countries, setCountries] = useState<
  {
    label: string;
    value: string;
  }[]
  >([]);
  const { language } = useLocalization();
  const validationSchema = yup.object().shape({
    firstName: yup.string().required(t('First name is required')),
    lastName: yup.string().required(t('Last name is required')),
    country: yup.string().required(t('Country is required')),
    email: yup.string().email(t('Invalid email')).required(t('Email is required')),
    websiteLink: yup.string().url(t('Invalid URL')),
    propertiesNumber: yup.string().required(t('Number of properties is required')),
    testedNuki: yup.boolean(),
    message: yup.string(),
    privacyPolicy: yup
      .boolean()
      .oneOf([true], t('You must accept the privacy policy'))
      .required(t('You must accept the privacy policy')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<B2BFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      country: '',
      email: '',
      websiteLink: '',
      propertiesNumber: '',
      testedNuki: false,
      message: '',
      privacyPolicy: false,
    },
  });

  const captchaHook = useCaptchaHook({
    siteKey: process.env.NEXT_PUBLIC_FRIENDLY_CAPTCHA_SITE_KEY || '',
    endpoint: FRIENDLY_CAPTCHA_ENDPOINT,
    startMode: 'none',
    language: language as any,
    showAttribution: true,
  });

  // clear error on captcha status change to success
  useEffect(() => {
    if (!captchaHook.captchaStatus.error && captchaHook.captchaStatus.solution) {
      setError('');
    }
  }, [captchaHook.captchaStatus]);

  async function onSubmit(data: B2BFormData) {
    if (captchaHook.captchaStatus.error || captchaHook.captchaStatus.solution === null) {
      setError(captchaMessages('Please complete the CAPTCHA before submitting'));
      return;
    }

    if (emailTo) {
      try {
        setError('');
        setLoading(true);
        const response = await axios.post(
          apiPaths.forms['b2b-form'],
          {
            emailTo,
            frcCaptchaSolution: captchaHook.captchaStatus.solution,
            ...data,
          },
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          },
        );
        setLoading(false);
        setEmailSent(true);
        DataLayerHelper.pushFormSubmit({ formId: 'b2b-contact-form' });
      } catch (requestError: any) {
        setLoading(false);
        console.error('Error sending email:', requestError);

        // Handle CAPTCHA-specific errors
        if (requestError.response && requestError.response.status === 400) {
          const serverCaptchaErrors = requestError.response.data.errors;
          console.error('captcha error', serverCaptchaErrors);
          if (
            serverCaptchaErrors?.includes(CaptchaValidationErrorCodes.SOLUTION_INVALID) ||
            serverCaptchaErrors?.includes(CaptchaValidationErrorCodes.SOLUTION_TIMEOUT_OR_DUPLICATE)
          ) {
            setError(captchaMessages('Captcha validation failed Please try again'));
            captchaHook.resetWidget();
            return;
          }
        }

        setError(t('Failed to send the email Please try again!'));
      }
    }
  }

  useEffect(() => {
    try {
      i18nCountries.registerLocale(require(`i18n-iso-countries/langs/${language}.json`));
      setCountries(
        Object.entries(i18nCountries.getNames(language, { select: 'official' })).map(
          ([value, label]) => ({
            value,
            label,
          }),
        ),
      );
    } catch (error) {
      console.error(error);
      i18nCountries.registerLocale(require('i18n-iso-countries/langs/en.json'));
      setCountries(
        Object.entries(i18nCountries.getNames('en', { select: 'official' })).map(
          ([value, label]) => ({
            value,
            label,
          }),
        ),
      );
    }
  }, []);

  return (
    <div className="bg-neutral-5">
      <Container>
        <div className="my-14 grid gap-10 md:grid-cols-2">
          <div>
            <RichTextRenderer blocks={tile.title} className="text-2xl md:text-3xl" />
            <RichTextRenderer blocks={tile.subtitle} className="mt-4 md:text-lg" />
            <div className="mt-6 space-y-5">
              {tile.feature?.chunks?.map((feature, index) => {
                const title = extractComponentFromChunk(
                  feature,
                  'title',
                  'SingleLineContent',
                )?.text;
                const subtitle = extractComponentFromChunk(feature, 'subtitle', 'RichTextContent');
                const icon = extractComponentFromChunk(feature, 'icon', 'ImageContent');
                return (
                  <div
                    key={`b2b-contact-form${index}`}
                    className="flex gap-4 md:items-center md:gap-6"
                  >
                    <OptimizedImage
                      imageComponent={icon}
                      sizes="100px"
                      width={100}
                      height={100}
                      className="h-12 w-auto md:h-20"
                    />
                    <div className="space-y-0">
                      <h4 className="font-bold md:text-lg">{title}</h4>
                      <RichTextRenderer blocks={subtitle} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="relative space-y-3 rounded-lg bg-white p-4 shadow-nuki-blured"
          >
            {emailSent && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 rounded-lg bg-white">
                <h3 className="text-center text-2xl font-bold">
                  {t('Thank you for your inquiry')}
                </h3>
                <Image
                  src={GreenCheckmark}
                  sizes="80px"
                  alt="Green checkmark"
                  className="h-14 w-auto md:h-20"
                  width={80}
                  height={80}
                />
              </div>
            )}
            <TextInput
              id="firstName"
              srLabel={t('First Name')}
              register={register}
              errors={errors.firstName}
              placeholder={t('First Name')}
            />
            <TextInput
              id="lastName"
              srLabel={t('Last Name')}
              register={register}
              errors={errors.lastName}
              placeholder={t('Last Name')}
            />
            <SelectInput
              name="country"
              control={control}
              options={countries}
              placeholder={t('Select a country')}
              optionsClass="sm:max-h-lg"
            />
            <TextInput
              id="email"
              srLabel={t('Email')}
              register={register}
              errors={errors.email}
              placeholder={t('Email')}
            />
            <TextInput
              id="websiteLink"
              srLabel={t('Website Link')}
              register={register}
              errors={errors.websiteLink}
              placeholder={t('Website Link')}
            />
            <SelectInput
              name="propertiesNumber"
              control={control}
              options={[
                { label: '1-19', value: '1-19' },
                { label: '20-49', value: '20-49' },
                { label: '50-99', value: '50-99' },
                { label: '100+', value: '100+' },
              ]}
              placeholder={t('Number of properties')}
            />
            {/* Have you tested Nuki? */}
            <div className="">
              <p className="font-bold">{t('Have you already tested Nuki?')}</p>
              <div className="flex items-center gap-2">
                <input type="radio" id="yes" {...register('testedNuki')} value="true" />
                <label htmlFor="yes">{t('Yes')}</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="radio" id="no" {...register('testedNuki')} value="false" />
                <label htmlFor="no">{t('No')}</label>
              </div>
            </div>
            {/* Message textarea */}
            <div>
              <p className="font-bold">{t('Message')}</p>
              <textarea
                id="message"
                {...register('message')}
                className="h- h-50 w-full rounded-xs border border-neutral-50 p-3"
                placeholder={t('Message Placeholder')}
              />
            </div>
            <PrivacyPolicyCheckBox register={register} errors={errors.privacyPolicy} />

            {captchaHook.CaptchaWidget({
              className: 'my-5 relative pb-4',
            })}

            <Button
              disabled={
                captchaHook.captchaStatus.solution === null ||
                captchaHook.captchaStatus.error !== null
              }
              type="submit"
              className="w-full"
              loading={loading}
              data-tile-element-id={buildTrackingId(trackingPrefix, 'submit')}
            >
              {t('Submit')}
            </Button>
            {error && <p className="mt-1 text-center text-sm text-red">{error}</p>}
          </form>
        </div>
      </Container>
    </div>
  );
}
